/**
 * The browser side of security keys and passkeys (WebAuthn), for allauth
 * headless. Dependency-free so node:test can import it (see webauthn.test.js).
 *
 * allauth hands out options as JSON, in the shape the WebAuthn spec calls
 * PublicKeyCredentialCreationOptionsJSON / RequestOptionsJSON, wrapped as
 * `{ publicKey: {...} }` (binary fields are base64url strings). The browser's
 * navigator.credentials wants ArrayBuffers, and allauth wants the resulting
 * credential back as JSON (RegistrationResponseJSON / AuthenticationResponseJSON).
 *
 * Current browsers do both conversions natively:
 * PublicKeyCredential.parseCreationOptionsFromJSON / parseRequestOptionsFromJSON
 * and credential.toJSON(). The small fallback here covers browsers that do not
 * have them yet (older Safari and Firefox), so no library is needed.
 */

// ── base64url ─────────────────────────────────────────────────────────

export function base64urlToBuffer(value) {
    const base64 = String(value).replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
    }
    return bytes.buffer
}

export function bufferToBase64url(buffer) {
    const bytes =
        buffer instanceof ArrayBuffer
            ? new Uint8Array(buffer)
            : new Uint8Array(
                  buffer.buffer,
                  buffer.byteOffset,
                  buffer.byteLength
              )
    let binary = ''
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

// ── Support ───────────────────────────────────────────────────────────

/** Whether this browser can use security keys and passkeys at all. */
export function isWebAuthnSupported(win = globalThis) {
    return Boolean(
        win?.PublicKeyCredential &&
            win?.navigator?.credentials?.create &&
            win?.navigator?.credentials?.get &&
            win?.isSecureContext !== false
    )
}

// ── Options: JSON -> what navigator.credentials wants ─────────────────

// allauth wraps the options as { publicKey: {...} }; accept either form.
function unwrap(options) {
    return options?.publicKey ?? options
}

function decodeDescriptors(list) {
    return (list || []).map((descriptor) => ({
        ...descriptor,
        id: base64urlToBuffer(descriptor.id)
    }))
}

export function parseCreationOptions(
    options,
    PKC = globalThis.PublicKeyCredential
) {
    const json = unwrap(options)
    if (typeof PKC?.parseCreationOptionsFromJSON === 'function') {
        return PKC.parseCreationOptionsFromJSON(json)
    }
    return {
        ...json,
        challenge: base64urlToBuffer(json.challenge),
        user: { ...json.user, id: base64urlToBuffer(json.user.id) },
        excludeCredentials: decodeDescriptors(json.excludeCredentials)
    }
}

export function parseRequestOptions(
    options,
    PKC = globalThis.PublicKeyCredential
) {
    const json = unwrap(options)
    if (typeof PKC?.parseRequestOptionsFromJSON === 'function') {
        return PKC.parseRequestOptionsFromJSON(json)
    }
    const parsed = { ...json, challenge: base64urlToBuffer(json.challenge) }
    if (json.allowCredentials) {
        parsed.allowCredentials = decodeDescriptors(json.allowCredentials)
    }
    return parsed
}

// ── Credential -> JSON for allauth ────────────────────────────────────

export function credentialToJSON(credential) {
    if (typeof credential?.toJSON === 'function') {
        return credential.toJSON()
    }
    const { response } = credential
    const json = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment ?? null,
        clientExtensionResults: credential.getClientExtensionResults
            ? credential.getClientExtensionResults()
            : {},
        response: {
            clientDataJSON: bufferToBase64url(response.clientDataJSON)
        }
    }
    if (response.attestationObject) {
        json.response.attestationObject = bufferToBase64url(
            response.attestationObject
        )
        if (typeof response.getTransports === 'function') {
            json.response.transports = response.getTransports()
        }
    }
    if (response.authenticatorData) {
        json.response.authenticatorData = bufferToBase64url(
            response.authenticatorData
        )
        json.response.signature = bufferToBase64url(response.signature)
        if (response.userHandle) {
            json.response.userHandle = bufferToBase64url(response.userHandle)
        }
    }
    return json
}

// ── The two ceremonies ────────────────────────────────────────────────

export class WebAuthnUnsupportedError extends Error {
    constructor() {
        super('WebAuthn is not available in this browser')
        this.name = 'WebAuthnUnsupportedError'
    }
}

/** Register a new key: allauth creation options in, credential JSON out. */
export async function createCredential(creationOptions, win = globalThis) {
    if (!isWebAuthnSupported(win)) throw new WebAuthnUnsupportedError()
    const publicKey = parseCreationOptions(
        creationOptions,
        win.PublicKeyCredential
    )
    const credential = await win.navigator.credentials.create({ publicKey })
    return credentialToJSON(credential)
}

/** Use a key: allauth request options in, credential JSON out. */
export async function getCredential(requestOptions, win = globalThis) {
    if (!isWebAuthnSupported(win)) throw new WebAuthnUnsupportedError()
    const publicKey = parseRequestOptions(
        requestOptions,
        win.PublicKeyCredential
    )
    const credential = await win.navigator.credentials.get({ publicKey })
    return credentialToJSON(credential)
}

// ── Errors people can act on ──────────────────────────────────────────

export const WEBAUTHN_MESSAGES = Object.freeze({
    unsupported:
        'This browser cannot use security keys or passkeys. Try an up-to-date version of Chrome, Edge, Safari or Firefox.',
    cancelled:
        'The request was cancelled or timed out. Try again when your security key or device is ready.',
    alreadyRegistered:
        'That security key or passkey is already set up on your account.',
    insecure:
        'Security keys and passkeys only work on a secure (https) connection to this site.',
    failed: 'That did not work. Try again, or use another way to sign in.',
    notRecognised:
        'That security key or passkey was not recognised for this account. Try another one, or use another way to sign in.'
})

/**
 * Turn whatever navigator.credentials threw into a message for the person.
 * The browser reports cancelling, timing out and "not allowed" all as
 * NotAllowedError on purpose (so sites cannot tell which), so they share a
 * message. `registering` marks the create ceremony, where InvalidStateError
 * means the key is already on the account (allauth lists existing keys in
 * excludeCredentials).
 */
export function describeWebAuthnError(error, { registering = false } = {}) {
    switch (error?.name) {
        case 'WebAuthnUnsupportedError':
        case 'NotSupportedError':
            return WEBAUTHN_MESSAGES.unsupported
        case 'NotAllowedError':
        case 'AbortError':
            return WEBAUTHN_MESSAGES.cancelled
        case 'InvalidStateError':
            return registering
                ? WEBAUTHN_MESSAGES.alreadyRegistered
                : WEBAUTHN_MESSAGES.failed
        case 'SecurityError':
            return WEBAUTHN_MESSAGES.insecure
        default:
            return WEBAUTHN_MESSAGES.failed
    }
}

/**
 * Errors for FormErrors: allauth's own (a 400 with `errors`) when the server
 * refused the credential, otherwise the browser's, explained.
 */
export function webAuthnErrors(error, options) {
    const serverErrors = error?.response?.data?.errors
    if (serverErrors?.length) {
        // allauth reports a credential it cannot verify as "Incorrect code.",
        // wording meant for typed codes.
        return serverErrors.map((e) =>
            e.code === 'incorrect_code'
                ? { ...e, message: WEBAUTHN_MESSAGES.notRecognised }
                : e
        )
    }
    return [{ message: describeWebAuthnError(error, options) }]
}
